import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTO_CLOSE_DELAY, AUTO_CLOSE_START_DELAY } from './constants';

export const useAutoClose = (isAppbarOpen: boolean, setIsAppbarOpen: (isOpen: boolean) => void) => {
  const [isLocked, setIsLocked] = useState(() => {
    const savedLockState = localStorage.getItem('appBarLocked');
    return savedLockState === 'true';
  });
  const [countdownProgress, setCountdownProgress] = useState(0);
  const autoCloseTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoCloseTimer = useCallback(() => {
    if (isLocked) return;

    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
    }

    autoCloseTimerRef.current = setTimeout(() => {
      setCountdownProgress(0);
      countdownTimerRef.current = setInterval(() => {
        setCountdownProgress(prev => {
          const newProgress = prev + 100 / (AUTO_CLOSE_DELAY / 100);
          if (newProgress >= 100) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
            }
            setIsAppbarOpen(false);
            return 0;
          }
          return newProgress;
        });
      }, 100);
    }, AUTO_CLOSE_START_DELAY);
  }, [isLocked]);

  const clearAutoCloseTimer = useCallback(() => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    setCountdownProgress(0);
  }, []);

  const toggleLock = useCallback(() => {
    const newLockState = !isLocked;
    setIsLocked(newLockState);
    localStorage.setItem('appBarLocked', String(newLockState));
    if (newLockState) {
      clearAutoCloseTimer();
    } else {
      startAutoCloseTimer();
    }
  }, [isLocked, clearAutoCloseTimer, startAutoCloseTimer]);

  useEffect(() => {
    if (isAppbarOpen && !isLocked) {
      startAutoCloseTimer();
    } else {
      clearAutoCloseTimer();
    }

    return () => {
      clearAutoCloseTimer();
    };
  }, [isAppbarOpen, isLocked, startAutoCloseTimer, clearAutoCloseTimer]);
  return {
    countdownProgress,
    isLocked,
    toggleLock,
    startAutoCloseTimer,
    clearAutoCloseTimer,
  };
};
